import { Container, Grid } from '@mui/material';
import AgeGateModal from '@subflow-frontend/sections/listing/AgeGateModal';
import { Product, SubscriberProductResponseDto } from '@subflow/api-client';
import { unprotectedSubscriberApi } from '@subflow/data-access';
import { NextSeo } from 'next-seo';
import { useState } from 'react';
import { AgeGateProvider } from '@subflow-frontend/sections/listing/AgeGateContext';
import { NextPageWithLayout } from '@subflow-frontend/layouts/types';
import Page from '@subflow-frontend/components/Page';
import ThanksForSubscribing from '@subflow-frontend/components/listing/ThanksForSubscribing';
import {
  SubscriptionCheckoutButton,
  SubscriptionSimplePrice,
  SubscriptionDetailsCarousel,
  SubscriptionSimpleSummary,
} from '@subflow-frontend/sections/@dashboard/store/subscription-details';
import { SkeletonProduct } from '@subflow-frontend/components/skeleton';
import SubscriberLayoutWrapper from '@subflow-frontend/layouts/SubscriberLayout';
import { subscriberTheme } from '@subflow-frontend/theme';

// ----------------------------------------------------------------------

interface SubscriberListingDetailsProps {
  initialProduct: SubscriberProductResponseDto;
}

const SubscriberListingDetails: NextPageWithLayout<
  SubscriberListingDetailsProps
> = ({ initialProduct }) => {
  const product = initialProduct;
  const [isSuccessful, setIsSuccessful] = useState(false);

  const {
    prices: [firstPrice],
  } = product;

  return (
    <Page title="Subscription Details">
      <AgeGateProvider product={product}>
        <AgeGateModal />

        {isSuccessful ? (
          <ThanksForSubscribing signUpRedirectUrl={product.signUpRedirectUrl} />
        ) : (
          <Container maxWidth={'xl'}>
            {product ? (
              <Grid container>
                <Grid item xs={12} md={6} lg={7}>
                  <SubscriptionDetailsCarousel
                    images={product.images?.map((img) => img.url)}
                  />
                  <SubscriptionSimpleSummary product={product as Product} />
                </Grid>
                <Grid item xs={12} md={6} lg={5}>
                  {firstPrice.unitAmount > 0 ? (
                    <SubscriptionCheckoutButton product={product as Product} />
                  ) : (
                    <SubscriptionSimplePrice
                      product={product as Product}
                      successCallback={() => {
                        setIsSuccessful(true);
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            ) : (
              <SkeletonProduct />
            )}
          </Container>
        )}
      </AgeGateProvider>
    </Page>
  );
};

SubscriberListingDetails.appTheme = subscriberTheme;

SubscriberListingDetails.getMetaTags = function getMetaTags(pageProps) {
  const product = pageProps.initialProduct;

  const openGraphImages =
    product.images.length > 0
      ? product.images.map((img) => {
          return {
            url: img.url,
          };
        })
      : product.merchant.logo
      ? [{ url: product.merchant.logo }]
      : [];
  return (
    <NextSeo
      title="Subscription Details"
      openGraph={{
        title: `${product.name} - ${product.merchant.businessName}`,
        description: product.description.substring(0, 200),
        images: openGraphImages,
        siteName: 'Subflow',
      }}
      twitter={{
        handle: '@handle',
        site: '@site',
        cardType: 'summary_large_image',
      }}
    />
  );
};

SubscriberListingDetails.getLayout = function getLayout(page, pageProps) {
  const logo = pageProps.initialProduct.merchant.logo;

  return (
    <SubscriberLayoutWrapper merchantLogo={logo}>
      {page}
    </SubscriberLayoutWrapper>
  );
};

export const getServerSideProps = async ({ query, params, req }) => {
  const subdomain = params.subdomain as string;
  const slug = params.slug as string;

  const product = (
    await unprotectedSubscriberApi.findOneUnprotectedProduct(subdomain, slug)
  ).data;
  if (!product) {
    return { notFound: true, props: undefined };
  }

  return { props: { initialProduct: product || {} } };
};

export default SubscriberListingDetails;
